import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { IntegradynLogo } from "@/components/integradyn-logo";
import { Menu, X, ChevronDown, Globe, BookOpen, Users, Phone, ArrowRight, Shield, TrendingUp, FolderOpen, MapPin, Tag, Headphones, CalendarCheck, GraduationCap, LayoutDashboard, Compass, ClipboardList } from "lucide-react";

// ─── Nav structure ─────────────────────────────────────────────────────────────

const aiAgentsItems = [
  { label: "AI Consulting Overview", href: "/ai-agents", desc: "We plan and build AI with you", icon: Compass },
  { label: "How the Program Works", href: "/ai-agents#how-it-works", desc: "Analysis → plan → build → support", icon: ClipboardList },
  { label: "What We Can Build", href: "/ai-agents#departments", desc: "AI employees for every department", icon: Users },
  { label: "Self-Hosted AI", href: "/ai-agents#local-first", desc: "AI that runs on your own hardware", icon: Shield },
];

const voiceItems = [
  { label: "Integradyn Voice Overview", href: "/voice", desc: "Your 24/7 AI receptionist", icon: Phone },
  { label: "What It Does", href: "/voice#features", desc: "Answers, quotes & books jobs", icon: Headphones },
  { label: "After the Call", href: "/voice#after-call", desc: "Confirmations, alerts & transcripts", icon: CalendarCheck },
  { label: "Train It Yourself", href: "/voice#training", desc: "Coach it live, by phone", icon: GraduationCap },
  { label: "Mission Control", href: "/voice#dashboard", desc: "Calls, leads & schedule dashboard", icon: LayoutDashboard },
];

const webDevItems = [
  { label: "Web Development Overview", href: "/web-development", desc: "Websites built to convert", icon: Globe },
  { label: "The Dashboard", href: "/web-development#dashboard", desc: "Your website's command center", icon: LayoutDashboard },
  { label: "Case Studies", href: "/case-studies/automatic-climate-hvac", desc: "Real results, real clients", icon: TrendingUp },
  { label: "Projects", href: "/projects", desc: "Our work portfolio", icon: FolderOpen },
  { label: "Pricing", href: "/pricing", desc: "Plans & packages", icon: Tag },
  { label: "Services Directory", href: "/services/directory", desc: "240 local SEO pages", icon: MapPin },
];

const sharedLinks = [
  { label: "Blog", href: "/blog", icon: BookOpen },
  { label: "About", href: "/about", icon: Users },
  { label: "Contact", href: "/contact", icon: Phone },
];

// ─── Desktop dropdown ──────────────────────────────────────────────────────────

interface DropdownProps {
  label: string;
  accentColor: string;
  items: { label: string; href: string; desc: string; icon: React.ElementType }[];
  isActive: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

function NavDropdown({ label, accentColor, items, isActive, isOpen, onOpen, onClose }: DropdownProps) {
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    onOpen();
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(onClose, 120);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
          isActive || isOpen
            ? "text-white bg-white/15"
            : "text-white/80 hover:text-white hover:bg-white/10"
        }`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        style={isActive ? { color: accentColor } : undefined}
        onClick={() => (isOpen ? onClose() : onOpen())}
        onFocus={onOpen}
      >
        {label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      <div
        className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
          isOpen ? "visible opacity-100 translate-y-0 pointer-events-auto" : "invisible opacity-0 -translate-y-1 pointer-events-none"
        }`}
        style={{ zIndex: 100 }}
      >
        <div className="w-64 rounded-2xl bg-[#0f172a]/95 border border-white/15 shadow-2xl backdrop-blur-2xl overflow-hidden">
          <div className="p-1.5">
            {items.map((item) => {
              const inner = (
                <>
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${accentColor}18`, border: `1px solid ${accentColor}30` }}
                  >
                    <item.icon className="w-4 h-4" style={{ color: accentColor }} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white leading-tight">{item.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5 leading-tight">{item.desc}</div>
                  </div>
                </>
              );
              const cls = "flex items-start gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-colors duration-150 hover:bg-white/8 group no-underline";
              const testId = `nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
              return item.href.includes("#") ? (
                <a key={item.href} href={item.href} className={cls} onClick={onClose} data-testid={testId}>
                  {inner}
                </a>
              ) : (
                <Link key={item.href} href={item.href}>
                  <span className={cls} onClick={onClose} data-testid={testId}>{inner}</span>
                </Link>
              );
            })}
          </div>
          <div className="border-t border-white/10 p-2">
            <Link href="/contact">
              <span
                className="flex items-center justify-center gap-2 w-full px-4 py-2 rounded-xl text-xs font-semibold text-white transition-all duration-200 cursor-pointer"
                style={{ background: `linear-gradient(to right, ${accentColor}cc, ${accentColor}88)` }}
                onClick={onClose}
                data-testid={`nav-dropdown-cta-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                Book a Free Consultation <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Mobile accordion section ─────────────────────────────────────────────────

interface MobilePathProps {
  label: string;
  accentColor: string;
  items: { label: string; href: string; desc: string; icon: React.ElementType }[];
  isActive: boolean;
  onNavigate: () => void;
}

function MobilePathSection({ label, accentColor, items, isActive, onNavigate }: MobilePathProps) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        className={`flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-200 ${
          isActive ? "bg-white/10" : "hover:bg-white/8"
        }`}
        style={isActive ? { color: accentColor } : { color: "rgba(255,255,255,0.85)" }}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      <div className={`overflow-hidden transition-all duration-200 ${open ? "max-h-96" : "max-h-0"}`}>
        <div className="pl-4 pb-1 space-y-0.5">
          {items.map((item) => {
            const inner = (
              <>
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${accentColor}18`, border: `1px solid ${accentColor}30` }}
                >
                  <item.icon className="w-3.5 h-3.5" style={{ color: accentColor }} />
                </div>
                <span className="text-sm text-white/80">{item.label}</span>
              </>
            );
            const cls = "flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-colors duration-150 hover:bg-white/8 no-underline";
            const testId = `mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
            return item.href.includes("#") ? (
              <a key={item.href} href={item.href} className={cls} onClick={onNavigate} data-testid={testId}>
                {inner}
              </a>
            ) : (
              <Link key={item.href} href={item.href}>
                <span className={cls} onClick={onNavigate} data-testid={testId}>{inner}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<"ai-agents" | "web-dev" | "voice" | null>(null);
  const [location] = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [mobileMenuOpen]);

  useEffect(() => { setMobileMenuOpen(false); }, [location]);

  const isPath = (prefix: string) => location.startsWith(prefix);

  const isSharedActive = (href: string) => {
    if (href === "/") return location === "/";
    return location.startsWith(href);
  };

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-[#0BC9B2] focus:text-black focus:rounded-lg focus:font-semibold focus:outline-none"
        data-testid="skip-nav-link"
      >
        Skip to main content
      </a>

      <header
        className={`sticky top-0 z-50 transition-all duration-500 border-b ${
          scrolled
            ? "bg-[#0f172a]/85 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] border-[#0BC9B2]/20"
            : "bg-[#0f172a]/45 backdrop-blur-xl shadow-[0_4px_16px_rgba(0,0,0,0.1)] border-white/10"
        }`}
        data-testid="header-main"
        role="banner"
      >
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex justify-between items-center h-16 md:h-20">

            {/* Logo */}
            <Link href="/" data-testid="logo-link">
              <span className="flex items-center cursor-pointer transition-transform duration-300 hover:scale-[1.02]">
                <IntegradynLogo variant="full" size="2xl" inverted={false} />
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-0.5" aria-label="Main navigation">

              {/* Web Development path */}
              <NavDropdown
                label="Websites"
                accentColor="#FF8A3D"
                items={webDevItems}
                isActive={isPath("/web-development") || isPath("/case-studies") || isPath("/projects") || isPath("/pricing") || isPath("/services")}
                isOpen={openMenu === "web-dev"}
                onOpen={() => setOpenMenu("web-dev")}
                onClose={() => setOpenMenu(null)}
              />

              {/* Integradyn Voice path */}
              <NavDropdown
                label="AI Receptionist"
                accentColor="#a78bfa"
                items={voiceItems}
                isActive={isPath("/voice")}
                isOpen={openMenu === "voice"}
                onOpen={() => setOpenMenu("voice")}
                onClose={() => setOpenMenu(null)}
              />

              {/* AI Consulting path */}
              <NavDropdown
                label="AI Consulting"
                accentColor="#0BC9B2"
                items={aiAgentsItems}
                isActive={isPath("/ai-agents")}
                isOpen={openMenu === "ai-agents"}
                onOpen={() => setOpenMenu("ai-agents")}
                onClose={() => setOpenMenu(null)}
              />

              {/* Shared links */}
              {sharedLinks.map((link) => (
                <Link key={link.href} href={link.href}>
                  <span
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                      isSharedActive(link.href)
                        ? "text-white bg-white/15"
                        : "text-white/80 hover:text-white hover:bg-white/10"
                    }`}
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </span>
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:+18667561020"
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-[#0BC9B2] hover:bg-[#0BC9B2]/10 transition-all duration-200 whitespace-nowrap"
                data-testid="nav-phone-link"
              >
                <Phone className="w-4 h-4 flex-shrink-0" />
                (866) 756-1020
              </a>
              <Link href="/contact">
                <span
                  className="inline-flex items-center gap-2 px-5 py-2.5 font-semibold text-sm rounded-full bg-gradient-to-r from-[#0BC9B2] to-[#FF8A3D] text-white shadow-md hover:shadow-lg hover:shadow-[#0BC9B2]/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  data-testid="cta-button-book"
                >
                  Book a Free Consultation
                </span>
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-2.5 rounded-xl transition-colors duration-200 relative z-50 hover:bg-white/15 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          mobileMenuOpen ? "visible" : "invisible"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
            mobileMenuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Drawer */}
        <div
          className={`absolute top-0 right-0 w-full max-w-sm h-full bg-[#0f172a] border-l border-white/10 shadow-2xl transition-transform duration-300 ease-out overflow-y-auto ${
            mobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col min-h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#0f172a] z-10">
              <IntegradynLogo variant="full" size="sm" inverted={false} />
              <button
                className="p-2 rounded-xl hover:bg-white/10 transition-colors text-white"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Nav */}
            <nav className="flex-1 py-3 px-3 space-y-0.5">
              {/* Home */}
              <Link href="/">
                <span
                  className={`flex items-center px-4 py-3.5 rounded-xl text-base font-medium cursor-pointer transition-all duration-200 ${
                    location === "/" ? "text-white bg-white/10" : "text-white/80 hover:text-white hover:bg-white/8"
                  }`}
                  data-testid="mobile-nav-link-home"
                >
                  Home
                </span>
              </Link>

              {/* Web Dev path */}
              <div className="pt-1 pb-0.5 px-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF8A3D]/60 px-3 pb-1">Websites + Dashboard</div>
              </div>
              <MobilePathSection
                label="Websites"
                accentColor="#FF8A3D"
                items={webDevItems}
                isActive={isPath("/web-development") || isPath("/case-studies") || isPath("/projects") || isPath("/pricing") || isPath("/services")}
                onNavigate={() => setMobileMenuOpen(false)}
              />

              {/* Integradyn Voice path */}
              <div className="pt-2 pb-0.5 px-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#a78bfa]/60 px-3 pb-1">Integradyn Voice</div>
              </div>
              <MobilePathSection
                label="AI Receptionist"
                accentColor="#a78bfa"
                items={voiceItems}
                isActive={isPath("/voice")}
                onNavigate={() => setMobileMenuOpen(false)}
              />

              {/* AI Consulting path */}
              <div className="pt-2 pb-0.5 px-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#0BC9B2]/60 px-3 pb-1">AI Consulting</div>
              </div>
              <MobilePathSection
                label="AI Consulting"
                accentColor="#0BC9B2"
                items={aiAgentsItems}
                isActive={isPath("/ai-agents")}
                onNavigate={() => setMobileMenuOpen(false)}
              />

              {/* Shared links */}
              <div className="pt-2 pb-0.5 px-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/30 px-3 pb-1">More</div>
              </div>
              {sharedLinks.map((link) => (
                <Link key={link.href} href={link.href}>
                  <span
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium cursor-pointer transition-all duration-200 ${
                      isSharedActive(link.href) ? "text-white bg-white/10" : "text-white/80 hover:text-white hover:bg-white/8"
                    }`}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  >
                    <link.icon className="w-4 h-4 opacity-60" />
                    {link.label}
                  </span>
                </Link>
              ))}
            </nav>

            {/* CTAs */}
            <div className="p-4 border-t border-white/10 space-y-3">
              <Link href="/contact">
                <span
                  className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-[#0BC9B2] to-[#FF8A3D] text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 cursor-pointer"
                  data-testid="mobile-cta-button"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Book a Free Consultation
                </span>
              </Link>
              <p className="text-center text-xs text-gray-500">20-minute call · No obligation</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
